import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GraftonChat } from "@/components/GraftonChat";
import NanocorpPipelineWidget from "@/components/crm/NanocorpPipelineWidget";
import { HubSpotPanel } from "@/components/crm/HubSpotPanel";
import { toast } from "sonner";
import {
  Users, Plus, Search, Download, Trash2, Send, ArrowLeft,
  Building2, Mail, Loader2, ChevronRight, Filter,
  FileText, Receipt, FolderKanban, ArrowRight,
  Phone, Edit3, Clock, FileDown, Bot,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { generateDocumentPDF } from "@/lib/generateDocumentPDF";

import RecommanderPartenaire from "@/components/RecommanderPartenaire";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface ContactProduct { id?: string; name: string; unit_price_eur: number; quantity: number; frequency: string; notes?: string; }
interface Contact { id: string; nom: string; prenom: string; email: string; telephone: string; entreprise: string; poste: string; statut: string; source: string; notes: string; tags: string[]; interactions: any[]; products?: ContactProduct[]; potential_mrr_eur?: number; potential_arr_eur?: number; potential_one_shot_eur?: number; nanocorp_enrolled?: boolean; created_at: string; updated_at: string; }
interface Quote { id: string; number: string; contact_name: string; title: string; items: any[]; total_ht: number; tva_rate: number; total_ttc: number; status: string; notes: string; created_at: string; }
interface Invoice { id: string; number: string; contact_name: string; title: string; items: any[]; total_ht: number; tva_rate: number; total_ttc: number; status: string; due_date: string; created_at: string; }
interface Project { id: string; name: string; contact_name: string; description: string; status: string; budget: number; deadline: string; created_at: string; }

const PIPELINE = [
  { id: "prospect", label: "Prospect", color: "bg-blue-500", text: "text-blue-400" },
  { id: "qualifie", label: "Qualifié", color: "bg-amber-500", text: "text-amber-400" },
  { id: "negociation", label: "Négociation", color: "bg-violet-500", text: "text-violet-400" },
  { id: "client", label: "Client", color: "bg-emerald-500", text: "text-emerald-400" },
  { id: "perdu", label: "Perdu", color: "bg-red-500", text: "text-red-400" },
];

const statusBadge = (statut: string) => {
  const s = PIPELINE.find(p => p.id === statut);
  if (!s) return <Badge variant="secondary">{statut}</Badge>;
  return <Badge variant="outline" className={`${s.text} border-current/30 text-[10px]`}><div className={`h-1.5 w-1.5 rounded-full ${s.color} mr-1`} />{s.label}</Badge>;
};

const docStatusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    brouillon: { cls: "text-muted-foreground", label: "Brouillon" },
    envoye: { cls: "text-blue-400", label: "Envoyé" },
    envoyee: { cls: "text-blue-400", label: "Envoyée" },
    accepte: { cls: "text-emerald-400", label: "Accepté" },
    refuse: { cls: "text-red-400", label: "Refusé" },
    payee: { cls: "text-emerald-400", label: "Payée" },
    en_retard: { cls: "text-red-400", label: "En retard" },
    en_cours: { cls: "text-blue-400", label: "En cours" },
    termine: { cls: "text-emerald-400", label: "Terminé" },
    en_attente: { cls: "text-amber-400", label: "En attente" },
    annule: { cls: "text-red-400", label: "Annulé" },
  };
  const s = map[status] || { cls: "text-muted-foreground", label: status };
  return <Badge variant="outline" className={`${s.cls} border-current/30 text-[10px]`}>{s.label}</Badge>;
};

const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

export default function CRMDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pipeline, setPipeline] = useState<Record<string, number>>({});
  const [dataLoading, setDataLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [importing, setImporting] = useState(false);

  // Dialogs
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddQuote, setShowAddQuote] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [newInteraction, setNewInteraction] = useState("");
  const [interactionType, setInteractionType] = useState("note");
  // Products on selected contact
  const [productForm, setProductForm] = useState({ name: "", unit_price_eur: "", quantity: "1", frequency: "monthly" });
  // Global potential
  const [potential, setPotential] = useState<{ potential_mrr_eur: number; potential_arr_eur: number; potential_one_shot_eur: number; contacts_with_products: number; by_stage: Record<string, { count: number; potential_mrr_eur: number; potential_arr_eur: number; potential_one_shot_eur: number }> } | null>(null);

  // Forms
  const [contactForm, setContactForm] = useState({ nom: "", prenom: "", email: "", telephone: "", entreprise: "", poste: "", statut: "prospect", notes: "" });
  const [quoteForm, setQuoteForm] = useState({ title: "", contact_name: "", total_ht: "", tva_rate: "20", notes: "" });
  const [invoiceForm, setInvoiceForm] = useState({ title: "", contact_name: "", total_ht: "", tva_rate: "20", due_date: "", notes: "" });
  const [projectForm, setProjectForm] = useState({ name: "", contact_name: "", description: "", budget: "", deadline: "" });

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [user, loading, navigate]);

  const fetchAll = useCallback(async () => {
    setDataLoading(true);
    try {
      const url = filterStatus ? `${API_URL}/api/crm/contacts?statut=${filterStatus}` : `${API_URL}/api/crm/contacts`;
      const [cRes, qRes, iRes, pRes] = await Promise.all([
        fetch(url, { credentials: "include" }),
        fetch(`${API_URL}/api/crm/quotes`, { credentials: "include" }),
        fetch(`${API_URL}/api/crm/invoices`, { credentials: "include" }),
        fetch(`${API_URL}/api/crm/projects`, { credentials: "include" }),
      ]);
      if (cRes.ok) { const d = await cRes.json(); setContacts(d.contacts || []); setPipeline(d.pipeline || {}); }
      if (qRes.ok) { const d = await qRes.json(); setQuotes(d.quotes || []); }
      if (iRes.ok) { const d = await iRes.json(); setInvoices(d.invoices || []); }
      if (pRes.ok) { const d = await pRes.json(); setProjects(d.projects || []); }
      // Potential revenue aggregation
      try {
        const potRes = await fetch(`${API_URL}/api/crm/potential`, { credentials: "include" });
        if (potRes.ok) setPotential(await potRes.json());
      } catch { /* noop */ }
    } catch {}
    setDataLoading(false);
  }, [filterStatus]);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  // CRUD handlers
  const createContact = async () => {
    if (!contactForm.nom) return;
    const res = await fetch(`${API_URL}/api/crm/contacts`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(contactForm) });
    if (res.ok) { toast.success("Contact créé !"); setShowAddContact(false); setContactForm({ nom: "", prenom: "", email: "", telephone: "", entreprise: "", poste: "", statut: "prospect", notes: "" }); fetchAll(); }
    else if (res.status === 401) toast.error("Session expirée");
  };

  const createQuote = async () => {
    if (!quoteForm.title) return;
    const ht = parseFloat(quoteForm.total_ht) || 0;
    const tva = parseFloat(quoteForm.tva_rate) || 20;
    const res = await fetch(`${API_URL}/api/crm/quotes`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...quoteForm, total_ht: ht, tva_rate: tva, total_ttc: ht * (1 + tva / 100) }) });
    if (res.ok) { toast.success("Devis créé !"); setShowAddQuote(false); setQuoteForm({ title: "", contact_name: "", total_ht: "", tva_rate: "20", notes: "" }); fetchAll(); }
  };

  const createInvoice = async () => {
    if (!invoiceForm.title) return;
    const ht = parseFloat(invoiceForm.total_ht) || 0;
    const tva = parseFloat(invoiceForm.tva_rate) || 20;
    const res = await fetch(`${API_URL}/api/crm/invoices`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...invoiceForm, total_ht: ht, tva_rate: tva, total_ttc: ht * (1 + tva / 100) }) });
    if (res.ok) { toast.success("Facture créée !"); setShowAddInvoice(false); setInvoiceForm({ title: "", contact_name: "", total_ht: "", tva_rate: "20", due_date: "", notes: "" }); fetchAll(); }
  };

  const startEditInvoice = (inv: Invoice) => {
    setEditingInvoice(inv);
    setInvoiceForm({
      title: inv.title || "",
      contact_name: inv.contact_name || "",
      total_ht: String(inv.total_ht ?? ""),
      tva_rate: String(inv.tva_rate ?? "20"),
      due_date: inv.due_date || "",
      notes: (inv as any).notes || "",
    });
    setShowAddInvoice(true);
  };

  const saveEditInvoice = async () => {
    if (!editingInvoice) return;
    const ht = parseFloat(invoiceForm.total_ht) || 0;
    const tva = parseFloat(invoiceForm.tva_rate) || 20;
    const res = await fetch(`${API_URL}/api/crm/invoices/${editingInvoice.id}`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...invoiceForm, total_ht: ht, tva_rate: tva, total_ttc: ht * (1 + tva / 100) }),
    });
    if (res.ok) {
      toast.success("Facture modifiée");
      setShowAddInvoice(false);
      setEditingInvoice(null);
      setInvoiceForm({ title: "", contact_name: "", total_ht: "", tva_rate: "20", due_date: "", notes: "" });
      fetchAll();
    } else toast.error("Modification impossible");
  };

  const deleteInvoice = async (inv: Invoice) => {
    if (!window.confirm(`Supprimer définitivement la facture ${inv.number || inv.title} ?`)) return;
    const res = await fetch(`${API_URL}/api/crm/invoices/${inv.id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Facture supprimée");
      fetchAll();
    } else toast.error("Suppression impossible");
  };

  const createProject = async () => {
    if (!projectForm.name) return;
    const res = await fetch(`${API_URL}/api/crm/projects`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...projectForm, budget: parseFloat(projectForm.budget) || 0 }) });
    if (res.ok) { toast.success("Projet créé !"); setShowAddProject(false); setProjectForm({ name: "", contact_name: "", description: "", budget: "", deadline: "" }); fetchAll(); }
  };

  const updateContactStatus = async (id: string, statut: string) => {
    await fetch(`${API_URL}/api/crm/contacts/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ statut }) });
    if (selectedContact?.id === id) setSelectedContact(prev => prev ? { ...prev, statut } : null);    fetchAll();
    toast.success("Statut mis à jour");
  };

  const deleteContact = async (id: string) => {
    await fetch(`${API_URL}/api/crm/contacts/${id}`, { method: "DELETE", credentials: "include" });
    setSelectedContact(null);
    fetchAll();
    toast.success("Contact supprimé");
  };

  const addInteraction = async () => {
    if (!selectedContact || !newInteraction.trim()) return;
    const res = await fetch(`${API_URL}/api/crm/contacts/${selectedContact.id}/interaction`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: interactionType, content: newInteraction }) });
    if (res.ok) { const i = await res.json(); setSelectedContact(prev => prev ? { ...prev, interactions: [...(prev.interactions || []), i] } : null); setNewInteraction(""); toast.success("Interaction ajoutée"); }
  };

  const saveProducts = async (products: ContactProduct[]) => {
    if (!selectedContact) return;
    const res = await fetch(`${API_URL}/api/crm/contacts/${selectedContact.id}/products`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ products }),
    });
    if (res.ok) {
      const d = await res.json();
      setSelectedContact(prev => prev ? { ...prev, products: d.products, potential_mrr_eur: d.potential_mrr_eur, potential_arr_eur: d.potential_arr_eur, potential_one_shot_eur: d.potential_one_shot_eur } : null);
      try {
        const p = await fetch(`${API_URL}/api/crm/potential`, { credentials: "include" });
        if (p.ok) setPotential(await p.json());
      } catch { /* noop */ }
      toast.success("Produits mis à jour");
    }
  };

  const addProductToSelected = async () => {
    if (!selectedContact || !productForm.name.trim()) return;
    const newP: ContactProduct = {
      id: crypto.randomUUID(),
      name: productForm.name.trim(),
      unit_price_eur: parseFloat(productForm.unit_price_eur) || 0,
      quantity: parseInt(productForm.quantity) || 1,
      frequency: productForm.frequency,
    };
    const next = [...(selectedContact.products || []), newP];
    await saveProducts(next);
    setProductForm({ name: "", unit_price_eur: "", quantity: "1", frequency: "monthly" });
  };

  const removeProductFromSelected = async (idx: number) => {
    if (!selectedContact) return;
    const next = (selectedContact.products || []).filter((_, i) => i !== idx);
    await saveProducts(next);
  };

  const handleImportAxonaut = async () => {
    setImporting(true);
    const res = await fetch(`${API_URL}/api/crm/import-axonaut`, { method: "POST", credentials: "include" });
    if (res.ok) { const d = await res.json(); toast.success(`${d.imported} contact(s) importé(s)`); fetchAll(); }
    setImporting(false);
  };

  const toggleNanocorp = async (id: string) => {
    const r = await fetch(`${API_URL}/api/nanocorp/pipeline/contact/${id}/toggle`, { method: "POST", credentials: "include" });
    if (r.ok) {
      const d = await r.json();
      setContacts(prev => prev.map(c => c.id === id ? { ...c, nanocorp_enrolled: d.enrolled } : c));
      toast.success(d.enrolled ? "Inscrit dans le pipeline NanoCorp" : "Retiré du pipeline NanoCorp");
    } else {
      toast.error("Échec mise à jour");
    }
  };

  const filtered = contacts.filter(c => { const q = search.toLowerCase(); return !q || `${c.prenom} ${c.nom} ${c.email} ${c.entreprise}`.toLowerCase().includes(q); });
  const totalContacts = Object.values(pipeline).reduce((s, v) => s + v, 0);

  if (loading || dataLoading) return (
    <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
  );

  return (
    <div className="min-h-screen bg-background">
      <main className="overflow-y-auto h-screen">
          {/* Header */}
          <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-3 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => navigate("/dashboard")} data-testid="crm-back-dashboard">
                  <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
                </Button>
                <div className="h-5 w-px bg-border/50" />
                <div>
                  <h1 className="text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> CRM Grafton</h1>
                  <p className="text-[10px] text-muted-foreground">{totalContacts} contact(s) — {quotes.length} devis — {invoices.length} facture(s) — {projects.length} projet(s)</p>
                </div>
              </div>
              <div className="flex gap-2" />

            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* HubSpot sync panel */}
            <HubSpotPanel onChange={fetchAll} />

            {/* NanoCorp Pipeline widget */}
            <NanocorpPipelineWidget onChange={fetchAll} />

            {/* Pipeline Kanban */}
            <div className="grid grid-cols-5 gap-3" data-testid="crm-pipeline">
              {PIPELINE.map((stage, stageIdx) => {
                const stageContacts = contacts.filter(c => c.statut === stage.id);
                const isActive = filterStatus === stage.id;
                const nextStage = stageIdx < PIPELINE.length - 1 ? PIPELINE[stageIdx + 1] : null;
                const prevStage = stageIdx > 0 ? PIPELINE[stageIdx - 1] : null;
                return (
                  <div key={stage.id} className="space-y-2">
                    <div
                      className={`rounded-xl border p-3 cursor-pointer transition-all ${isActive ? "border-primary/40 bg-card/80" : "border-border/40 bg-card/30 hover:border-primary/20"}`}
                      onClick={() => setFilterStatus(isActive ? "" : stage.id)}
                      data-testid={`pipeline-${stage.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <div className={`h-2 w-2 rounded-full ${stage.color}`} />
                          <span className="text-xs font-semibold">{stage.label}</span>
                        </div>
                        <span className="text-lg font-bold">{pipeline[stage.id] || 0}</span>
                      </div>
                    </div>
                    {/* Contact cards in pipeline */}
                    <div className="space-y-1.5 max-h-[200px] overflow-auto">
                      {stageContacts.map(c => (
                        <div
                          key={c.id}
                          className="rounded-lg bg-muted/20 border border-border/20 p-2 text-[10px] group hover:border-primary/20 transition-colors relative"
                        >
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleNanocorp(c.id); }}
                            className={`absolute top-1 right-1 h-4 w-4 rounded flex items-center justify-center transition-colors ${
                              c.nanocorp_enrolled ? "text-primary bg-primary/15 hover:bg-primary/25" : "text-muted-foreground/40 hover:text-muted-foreground bg-transparent hover:bg-muted/40"
                            }`}
                            title={c.nanocorp_enrolled ? "Inscrit pipeline NanoCorp — clic pour exclure" : "Hors pipeline — clic pour inscrire"}
                            data-testid={`nanocorp-toggle-${c.id}`}
                          >
                            <Bot className="h-2.5 w-2.5" />
                          </button>
                          <div className="flex items-center gap-1 cursor-pointer pr-5" onClick={() => setSelectedContact(c)}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{c.prenom} {c.nom}</p>
                              {c.entreprise && <p className="text-muted-foreground truncate">{c.entreprise}</p>}
                            </div>
                          </div>
                          {/* Navigation arrows */}
                          <div className="flex justify-between mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {prevStage ? (
                              <button
                                className="text-[8px] px-1.5 py-0.5 rounded bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                onClick={(e) => { e.stopPropagation(); updateContactStatus(c.id, prevStage.id); }}
                                title={`Vers ${prevStage.label}`}
                              >
                                ← {prevStage.label}
                              </button>
                            ) : <span />}
                            {nextStage && (
                              <button
                                className="text-[8px] px-1.5 py-0.5 rounded bg-primary/15 hover:bg-primary/30 text-primary transition-colors"
                                onClick={(e) => { e.stopPropagation(); updateContactStatus(c.id, nextStage.id); }}
                                title={`Vers ${nextStage.label}`}
                              >
                                {nextStage.label} →
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* + Button to add contact directly to this stage */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full h-7 text-[10px] text-muted-foreground hover:text-foreground gap-1 border border-dashed border-border/30 hover:border-primary/30"
                      onClick={() => { setContactForm({ ...contactForm, statut: stage.id }); setShowAddContact(true); }}
                      data-testid={`pipeline-add-${stage.id}`}
                    >
                      <Plus className="h-3 w-3" /> Ajouter
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Potential revenue widget */}
            {potential && (potential.potential_mrr_eur > 0 || potential.contacts_with_products > 0) && (
              <Card className="border-amber-500/30 bg-amber-500/5" data-testid="crm-potential-widget">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-amber-400">Potentiel revenu CRM</div>
                      <div className="text-2xl font-bold mt-0.5">
                        {fmt(potential.potential_mrr_eur)} <span className="text-sm font-normal text-muted-foreground">/ mois</span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5">
                        soit {fmt(potential.potential_arr_eur)} en récurrent annuel
                        {potential.potential_one_shot_eur > 0 && <> · + {fmt(potential.potential_one_shot_eur)} en one-shot</>}
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      {potential.contacts_with_products} contact(s) avec produits<br />
                      sur {contacts.length} au total
                    </div>
                  </div>
                  {Object.keys(potential.by_stage).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs pt-2 border-t border-amber-500/20">
                      {PIPELINE.map(s => {
                        const st = potential.by_stage[s.id];
                        if (!st || st.potential_mrr_eur === 0) return null;
                        return (
                          <div key={s.id} className="bg-background/40 rounded p-2">
                            <div className="flex items-center gap-1">
                              <div className={`h-1.5 w-1.5 rounded-full ${s.color}`} />
                              <span className={s.text}>{s.label}</span>
                            </div>
                            <div className="font-semibold">{fmt(st.potential_mrr_eur)}/m</div>
                            <div className="text-muted-foreground text-[10px]">{st.count} contacts</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <Tabs defaultValue="contacts" className="space-y-4">
              <TabsList className="bg-muted/50">
                <TabsTrigger value="contacts" className="gap-1.5 text-xs" data-testid="tab-contacts"><Users className="h-3.5 w-3.5" /> Contacts</TabsTrigger>
                <TabsTrigger value="quotes" className="gap-1.5 text-xs" data-testid="tab-quotes"><FileText className="h-3.5 w-3.5" /> Devis</TabsTrigger>
                <TabsTrigger value="invoices" className="gap-1.5 text-xs" data-testid="tab-invoices"><Receipt className="h-3.5 w-3.5" /> Factures</TabsTrigger>
                <TabsTrigger value="projects" className="gap-1.5 text-xs" data-testid="tab-projects"><FolderKanban className="h-3.5 w-3.5" /> Projets</TabsTrigger>
              </TabsList>

              {/* ── CONTACTS ── */}
              <TabsContent value="contacts" className="space-y-3">
                <div className="flex gap-3">
                  <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="crm-search" /></div>
                  {filterStatus && <Button variant="outline" size="sm" onClick={() => setFilterStatus("")} className="gap-1 text-xs"><Filter className="h-3 w-3" /> Tout</Button>}
                  <Button size="sm" className="gap-1.5" onClick={() => setShowAddContact(true)} data-testid="add-contact-btn"><Plus className="h-3.5 w-3.5" /> Contact</Button>
                </div>
                {filtered.length === 0 ? (
                  <Card className="border-border/50"><CardContent className="p-10 text-center"><Users className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" /><p className="text-sm text-muted-foreground">Aucun contact</p></CardContent></Card>
                ) : filtered.map(c => (
                  <Card key={c.id} className="border-border/50 hover:border-primary/20 transition-all cursor-pointer" onClick={() => setSelectedContact(c)} data-testid={`contact-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><span className="text-sm font-bold text-primary">{(c.prenom?.[0] || "")}{(c.nom?.[0] || "?")}</span></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2"><span className="text-sm font-semibold">{c.prenom} {c.nom}</span>{statusBadge(c.statut)}{c.source === "axonaut" && <Badge variant="outline" className="text-[8px] px-1">Axonaut</Badge>}</div>
                          <p className="text-xs text-muted-foreground truncate">{c.entreprise && <><Building2 className="h-3 w-3 inline mr-1" />{c.entreprise}</>}{c.email && <><Mail className="h-3 w-3 inline mx-1 ml-2" />{c.email}</>}</p>
                        </div>
                        <div className="text-right shrink-0"><p className="text-[10px] text-muted-foreground">{c.updated_at ? formatDistanceToNow(new Date(c.updated_at), { addSuffix: true, locale: fr }) : ""}</p></div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/30" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              {/* ── DEVIS ── */}
              <TabsContent value="quotes" className="space-y-3">
                <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setShowAddQuote(true)} data-testid="add-quote-btn"><Plus className="h-3.5 w-3.5" /> Nouveau devis</Button></div>
                {quotes.length === 0 ? (
                  <Card className="border-border/50"><CardContent className="p-10 text-center"><FileText className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" /><p className="text-sm text-muted-foreground">Aucun devis</p></CardContent></Card>
                ) : (
                  <Card className="border-border/50"><CardContent className="p-0">
                    <div className="divide-y divide-border/30">
                      <div className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_40px] gap-4 px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20"><span>N°</span><span>Client</span><span>Titre</span><span>Montant HT</span><span>TTC</span><span>Statut</span><span></span></div>
                      {quotes.map(q => (
                        <div key={q.id} className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_40px] gap-4 px-5 py-3 hover:bg-muted/10 items-center" data-testid={`quote-${q.id}`}>
                          <span className="text-sm font-medium text-primary">{q.number}</span>
                          <span className="text-sm">{q.contact_name || "—"}</span>
                          <span className="text-sm text-muted-foreground truncate">{q.title}</span>
                          <span className="text-sm font-bold">{fmt(q.total_ht)}</span>
                          <span className="text-sm">{fmt(q.total_ttc)}</span>
                          <span>{docStatusBadge(q.status)}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            title="Télécharger PDF"
                            data-testid={`quote-pdf-${q.id}`}
                            onClick={() => {
                              generateDocumentPDF({
                                kind: "devis",
                                number: q.number,
                                title: q.title,
                                contact_name: q.contact_name,
                                items: q.items as any,
                                total_ht: q.total_ht,
                                tva_rate: q.tva_rate,
                                total_ttc: q.total_ttc,
                                status: q.status,
                                created_at: q.created_at,
                                notes: (q as any).notes,
                                company: { name: user?.name || "Grafton IA" },
                              });
                              toast.success(`Devis ${q.number} téléchargé`);
                            }}
                          >
                            <FileDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent></Card>
                )}
              </TabsContent>

              {/* ── FACTURES ── */}
              <TabsContent value="invoices" className="space-y-3">
                <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setShowAddInvoice(true)} data-testid="add-invoice-btn"><Plus className="h-3.5 w-3.5" /> Nouvelle facture</Button></div>
                {invoices.length === 0 ? (
                  <Card className="border-border/50"><CardContent className="p-10 text-center"><Receipt className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" /><p className="text-sm text-muted-foreground">Aucune facture</p></CardContent></Card>
                ) : (
                  <Card className="border-border/50"><CardContent className="p-0">
                    <div className="divide-y divide-border/30">
                      <div className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_120px] gap-4 px-5 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20"><span>N°</span><span>Client</span><span>Titre</span><span>TTC</span><span>Échéance</span><span>Statut</span><span className="text-center">Actions</span></div>
                      {invoices.map(inv => (
                        <div key={inv.id} className="grid grid-cols-[1fr_1fr_1.5fr_1fr_1fr_1fr_120px] gap-4 px-5 py-3 hover:bg-muted/10 items-center" data-testid={`invoice-${inv.id}`}>
                          <span className="text-sm font-medium text-primary">{inv.number}</span>
                          <span className="text-sm">{inv.contact_name || "—"}</span>
                          <span className="text-sm text-muted-foreground truncate">{inv.title}</span>
                          <span className="text-sm font-bold">{fmt(inv.total_ttc)}</span>
                          <span className="text-xs text-muted-foreground">{inv.due_date || "—"}</span>
                          <span>{docStatusBadge(inv.status)}</span>
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              title="Télécharger PDF"
                              data-testid={`invoice-pdf-${inv.id}`}
                              onClick={() => {
                                generateDocumentPDF({
                                  kind: "facture",
                                  number: inv.number,
                                  title: inv.title,
                                  contact_name: inv.contact_name,
                                  items: inv.items as any,
                                  total_ht: inv.total_ht,
                                  tva_rate: inv.tva_rate,
                                  total_ttc: inv.total_ttc,
                                  status: inv.status,
                                  due_date: inv.due_date,
                                  created_at: inv.created_at,
                                  company: { name: user?.name || "Grafton IA" },
                                });
                                toast.success(`Facture ${inv.number} téléchargée`);
                              }}
                            >
                              <FileDown className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              title="Modifier"
                              data-testid={`invoice-edit-${inv.id}`}
                              onClick={() => startEditInvoice(inv)}
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Supprimer"
                              data-testid={`invoice-delete-${inv.id}`}
                              onClick={() => deleteInvoice(inv)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent></Card>
                )}
              </TabsContent>

              {/* ── PROJETS ── */}
              <TabsContent value="projects" className="space-y-3">
                <div className="flex justify-end"><Button size="sm" className="gap-1.5" onClick={() => setShowAddProject(true)} data-testid="add-project-btn"><Plus className="h-3.5 w-3.5" /> Nouveau projet</Button></div>
                {projects.length === 0 ? (
                  <Card className="border-border/50"><CardContent className="p-10 text-center"><FolderKanban className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" /><p className="text-sm text-muted-foreground">Aucun projet</p></CardContent></Card>
                ) : projects.map(p => (
                  <Card key={p.id} className="border-border/50" data-testid={`project-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0"><FolderKanban className="h-5 w-5 text-violet-500" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2"><span className="text-sm font-semibold">{p.name}</span>{docStatusBadge(p.status)}</div>
                          <p className="text-xs text-muted-foreground truncate">{p.contact_name && `${p.contact_name} · `}{p.description || "Pas de description"}</p>
                        </div>
                        {p.budget > 0 && <span className="text-sm font-bold">{fmt(p.budget)}</span>}
                        {p.deadline && <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{p.deadline}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
            </Tabs>
          </div>
        </main>

        {/* ── DIALOGS ── */}

        {/* Add Contact */}
        <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Nouveau contact</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3"><Input placeholder="Prénom" value={contactForm.prenom} onChange={e => setContactForm({ ...contactForm, prenom: e.target.value })} data-testid="crm-prenom" /><Input placeholder="Nom *" value={contactForm.nom} onChange={e => setContactForm({ ...contactForm, nom: e.target.value })} data-testid="crm-nom" /></div>
              <Input placeholder="Email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} data-testid="crm-email" />
              <Input placeholder="Téléphone" value={contactForm.telephone} onChange={e => setContactForm({ ...contactForm, telephone: e.target.value })} />
              <div className="grid grid-cols-2 gap-3"><Input placeholder="Entreprise" value={contactForm.entreprise} onChange={e => setContactForm({ ...contactForm, entreprise: e.target.value })} /><Input placeholder="Poste" value={contactForm.poste} onChange={e => setContactForm({ ...contactForm, poste: e.target.value })} /></div>
              <Select value={contactForm.statut} onValueChange={v => setContactForm({ ...contactForm, statut: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PIPELINE.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select>
              <Textarea placeholder="Notes" value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} rows={2} />
              <Button className="w-full" onClick={createContact} disabled={!contactForm.nom} data-testid="crm-create-submit">Créer le contact</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Quote */}
        <Dialog open={showAddQuote} onOpenChange={setShowAddQuote}>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Nouveau devis</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Titre du devis *" value={quoteForm.title} onChange={e => setQuoteForm({ ...quoteForm, title: e.target.value })} data-testid="quote-title" />
              <Input placeholder="Client" value={quoteForm.contact_name} onChange={e => setQuoteForm({ ...quoteForm, contact_name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3"><Input placeholder="Montant HT (€)" type="number" value={quoteForm.total_ht} onChange={e => setQuoteForm({ ...quoteForm, total_ht: e.target.value })} data-testid="quote-amount" /><Input placeholder="TVA (%)" type="number" value={quoteForm.tva_rate} onChange={e => setQuoteForm({ ...quoteForm, tva_rate: e.target.value })} /></div>
              {quoteForm.total_ht && <p className="text-xs text-muted-foreground">TTC : {fmt(parseFloat(quoteForm.total_ht || "0") * (1 + parseFloat(quoteForm.tva_rate || "20") / 100))}</p>}
              <Textarea placeholder="Notes" value={quoteForm.notes} onChange={e => setQuoteForm({ ...quoteForm, notes: e.target.value })} rows={2} />
              <Button className="w-full" onClick={createQuote} disabled={!quoteForm.title} data-testid="quote-create-submit">Créer le devis</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Invoice */}
        <Dialog open={showAddInvoice} onOpenChange={(open) => { setShowAddInvoice(open); if (!open) { setEditingInvoice(null); setInvoiceForm({ title: "", contact_name: "", total_ht: "", tva_rate: "20", due_date: "", notes: "" }); } }}>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{editingInvoice ? "Modifier la facture" : "Nouvelle facture"}</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Titre *" value={invoiceForm.title} onChange={e => setInvoiceForm({ ...invoiceForm, title: e.target.value })} data-testid="invoice-title" />
              <Input placeholder="Client" value={invoiceForm.contact_name} onChange={e => setInvoiceForm({ ...invoiceForm, contact_name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3"><Input placeholder="Montant HT (€)" type="number" value={invoiceForm.total_ht} onChange={e => setInvoiceForm({ ...invoiceForm, total_ht: e.target.value })} data-testid="invoice-amount" /><Input placeholder="TVA (%)" type="number" value={invoiceForm.tva_rate} onChange={e => setInvoiceForm({ ...invoiceForm, tva_rate: e.target.value })} /></div>
              <Input type="date" placeholder="Échéance" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} />
              {invoiceForm.total_ht && <p className="text-xs text-muted-foreground">TTC : {fmt(parseFloat(invoiceForm.total_ht || "0") * (1 + parseFloat(invoiceForm.tva_rate || "20") / 100))}</p>}
              <Button className="w-full" onClick={editingInvoice ? saveEditInvoice : createInvoice} disabled={!invoiceForm.title} data-testid="invoice-create-submit">{editingInvoice ? "Enregistrer les modifications" : "Créer la facture"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Project */}
        <Dialog open={showAddProject} onOpenChange={setShowAddProject}>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Nouveau projet</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Nom du projet *" value={projectForm.name} onChange={e => setProjectForm({ ...projectForm, name: e.target.value })} data-testid="project-name" />
              <Input placeholder="Client" value={projectForm.contact_name} onChange={e => setProjectForm({ ...projectForm, contact_name: e.target.value })} />
              <Textarea placeholder="Description" value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} rows={2} />
              <div className="grid grid-cols-2 gap-3"><Input placeholder="Budget (€)" type="number" value={projectForm.budget} onChange={e => setProjectForm({ ...projectForm, budget: e.target.value })} /><Input type="date" placeholder="Deadline" value={projectForm.deadline} onChange={e => setProjectForm({ ...projectForm, deadline: e.target.value })} /></div>
              <Button className="w-full" onClick={createProject} disabled={!projectForm.name} data-testid="project-create-submit">Créer le projet</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Detail */}
        <Dialog open={!!selectedContact} onOpenChange={open => !open && setSelectedContact(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            {selectedContact && (<>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><span className="text-sm font-bold text-primary">{(selectedContact.prenom?.[0] || "")}{(selectedContact.nom?.[0] || "?")}</span></div>{selectedContact.prenom} {selectedContact.nom}</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedContact.entreprise && <div><span className="text-muted-foreground text-xs">Entreprise</span><p className="font-medium">{selectedContact.entreprise}</p></div>}
                  {selectedContact.email && <div><span className="text-muted-foreground text-xs">Email</span><p className="font-medium">{selectedContact.email}</p></div>}
                  {selectedContact.telephone && <div><span className="text-muted-foreground text-xs">Téléphone</span><p className="font-medium">{selectedContact.telephone}</p></div>}
                  {selectedContact.poste && <div><span className="text-muted-foreground text-xs">Poste</span><p className="font-medium">{selectedContact.poste}</p></div>}
                </div>
                <div><p className="text-xs text-muted-foreground mb-2">Pipeline</p>
                  <div className="flex gap-1.5 flex-wrap">{PIPELINE.map(s => (<Button key={s.id} variant={selectedContact.statut === s.id ? "default" : "outline"} size="sm" className="text-xs h-7" onClick={() => updateContactStatus(selectedContact.id, s.id)}><div className={`h-1.5 w-1.5 rounded-full ${s.color} mr-1`} />{s.label}</Button>))}</div>
                </div>
                <RecommanderPartenaire contactId={selectedContact.id} />
                {selectedContact.notes && <div className="p-3 bg-muted/30 rounded-lg border border-border/30"><p className="text-xs text-muted-foreground mb-1">Notes</p><p className="text-sm">{selectedContact.notes}</p></div>}

                {/* Produits & potentiel revenu */}
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3" data-testid="contact-products-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-amber-400 font-semibold uppercase tracking-wide">Produits & Potentiel</p>
                    {(selectedContact.potential_mrr_eur || 0) > 0 && (
                      <p className="text-sm font-semibold">{fmt(selectedContact.potential_mrr_eur || 0)} <span className="text-muted-foreground text-xs font-normal">/ mois</span></p>
                    )}
                  </div>
                  <div className="space-y-1 mb-2" data-testid="contact-products-list">
                    {(selectedContact.products || []).length === 0 && <p className="text-xs text-muted-foreground">Aucun produit associé pour l'instant.</p>}
                    {(selectedContact.products || []).map((p, i) => (
                      <div key={p.id || i} className="flex items-center gap-2 bg-background/40 rounded px-2 py-1.5 text-xs">
                        <span className="flex-1 truncate font-medium">{p.name}</span>
                        <span className="text-muted-foreground">{p.quantity > 1 ? `${p.quantity} × ` : ""}{fmt(p.unit_price_eur)} {p.frequency === "monthly" ? "/mois" : p.frequency === "yearly" ? "/an" : ""}</span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeProductFromSelected(i)} data-testid={`remove-product-${i}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <Input placeholder="Produit / prestation" value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })} className="h-8 text-xs" data-testid="product-name-input" />
                    <div className="flex gap-1">
                      <Input placeholder="Prix €" value={productForm.unit_price_eur} onChange={e => setProductForm({ ...productForm, unit_price_eur: e.target.value })} className="h-8 text-xs flex-1" data-testid="product-price-input" type="number" />
                      <Input placeholder="Qté" value={productForm.quantity} onChange={e => setProductForm({ ...productForm, quantity: e.target.value })} className="h-8 text-xs w-12" type="number" />
                    </div>
                    <Select value={productForm.frequency} onValueChange={(v) => setProductForm({ ...productForm, frequency: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensuel</SelectItem>
                        <SelectItem value="yearly">Annuel</SelectItem>
                        <SelectItem value="one_shot">One-shot</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 text-xs" onClick={addProductToSelected} disabled={!productForm.name.trim()} data-testid="add-product-btn"><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
                  </div>
                </div>

                <div><p className="text-xs text-muted-foreground mb-2">Historique ({(selectedContact.interactions || []).length})</p>
                  <div className="space-y-2 max-h-[150px] overflow-auto">{(selectedContact.interactions || []).slice().reverse().map(int => (<div key={int.id} className="flex items-start gap-2 p-2 rounded-lg bg-muted/20"><Badge variant="outline" className="text-[8px] shrink-0 mt-0.5">{int.type}</Badge><div className="flex-1"><p className="text-xs">{int.content}</p><p className="text-[9px] text-muted-foreground mt-0.5">{formatDistanceToNow(new Date(int.created_at), { addSuffix: true, locale: fr })}</p></div></div>))}</div>
                  <div className="flex gap-2 mt-3">
                    <Select value={interactionType} onValueChange={setInteractionType}><SelectTrigger className="w-[100px] h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="note">Note</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="appel">Appel</SelectItem><SelectItem value="reunion">Réunion</SelectItem><SelectItem value="relance">Relance</SelectItem></SelectContent></Select>
                    <Input placeholder="Ajouter..." value={newInteraction} onChange={e => setNewInteraction(e.target.value)} className="flex-1 h-9 text-xs" onKeyDown={e => e.key === "Enter" && addInteraction()} />
                    <Button size="sm" className="h-9" onClick={addInteraction} disabled={!newInteraction.trim()}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-border/30">
                  {selectedContact.email && <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => navigate("/emails")}><Send className="h-3 w-3" /> Email</Button>}
                  <Button variant="destructive" size="sm" className="gap-1.5 text-xs ml-auto" onClick={() => deleteContact(selectedContact.id)}><Trash2 className="h-3 w-3" /> Supprimer</Button>
                </div>
              </div>
            </>)}
          </DialogContent>
        </Dialog>

        <GraftonChat context={{ totalContacts, pipeline, quotes: quotes.length, invoices: invoices.length, projects: projects.length }} quickPrompts={["Quels prospects relancer ?", "Résumé du pipeline", "Créer un devis type"]} subtitle="Expert CRM" welcomeMessage="Gérez vos contacts, devis, factures et projets." />
    </div>
  );
}
